import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { MainService } from 'src/app/app.service';
import { SmartReviewApiService } from '../core/api/smart-review-api.service';
import {
  ReviewStats, ReviewStatsAttentionFilters, ReviewStatsAttentionPage, ReviewStatsFileFindings,
  ReviewStatsFileFindingsFilters, ReviewStatsFilters, ReviewStatsHotspotFilters, ReviewStatsHotspotPage
} from './interfaces/review-stats.interface';

/**
 * Serviço do Dashboard de Reviews. Consome `GET /reviews/stats`, servido tanto ao
 * usuário comum quanto à administração — a diferença é o bloco `health`, presente só
 * quando o backend reconhece a capability `smartreview.admin` no token.
 *
 * Sem `catchError(mainService.handleError)`, pelo mesmo motivo de `CostsService` e
 * `GatesService`: aquele handler troca o `HttpErrorResponse` por um `Error` genérico,
 * sem `status` e sem o corpo — e a tela precisa dos dois para tratar erro e exibir a
 * mensagem do backend.
 */
@Injectable()
export class ReviewStatsService extends SmartReviewApiService {
  private get base(): string { return `${this.apiURL}/reviews/stats`; }

  constructor(http: HttpClient, mainService: MainService) {
    super(http, mainService);
  }

  private buildParams(
    filters: ReviewStatsFilters | ReviewStatsHotspotFilters | ReviewStatsAttentionFilters
      | ReviewStatsFileFindingsFilters
  ): HttpParams {
    let params = new HttpParams();
    Object.keys(filters).forEach(key => {
      const value = (filters as Record<string, string | string[] | number | undefined>)[key];
      if (Array.isArray(value)) {
        value.filter(Boolean).forEach(item => {
          params = params.append(key, item); // array → query param repetido (groupBy, filtros)
        });
      } else if (value !== undefined && value !== '') {
        params = params.set(key, String(value)); // escalar → set (vazio nunca é enviado)
      }
    });
    return params;
  }

  /** GET /reviews/stats — totais, achados, duração, série e breakdowns pedidos. */
  getStats(filters: ReviewStatsFilters = {}): Observable<ReviewStats> {
    return this.http.get<ReviewStats>(this.base, {
      headers: this.jsonHeaders,
      params: this.buildParams(filters)
    });
  }

  /** GET /reviews/stats/hotspots — ranking pesquisado e paginado no backend. */
  getHotspots(filters: ReviewStatsHotspotFilters): Observable<ReviewStatsHotspotPage> {
    return this.http.get<ReviewStatsHotspotPage>(`${this.base}/hotspots`, {
      headers: this.jsonHeaders,
      params: this.buildParams(filters)
    });
  }

  /** GET /reviews/stats/attention — reviews que pedem ação, mais recentes primeiro. */
  getAttention(filters: ReviewStatsAttentionFilters): Observable<ReviewStatsAttentionPage> {
    return this.http.get<ReviewStatsAttentionPage>(`${this.base}/attention`, {
      headers: this.jsonHeaders,
      params: this.buildParams(filters)
    });
  }

  /** GET /reviews/stats/hotspots/findings — o que os reviews recentes apontaram num fonte. */
  getFileFindings(filters: ReviewStatsFileFindingsFilters): Observable<ReviewStatsFileFindings> {
    return this.http.get<ReviewStatsFileFindings>(`${this.base}/hotspots/findings`, {
      headers: this.jsonHeaders,
      params: this.buildParams(filters)
    });
  }
}
